import { PDFDocument } from "pdf-lib";

import { LABEL_GEOMETRY } from "@/lib/medication-label";

const PT_PER_IN = 72;

// The vendor page carries its own top/bottom margins, so the medication
// label's padTop/padBottom would double-pad; only padX is a hard constraint
// (the LabelWriter's unprintable leading/trailing edge).
const VERTICAL_INSET_IN = LABEL_GEOMETRY.padBottomIn;

/**
 * Redraw each page of a vendor label PDF onto a page sized to the label stock
 * the medication label prints on, scaled uniformly to fit. Content is copied
 * across untouched.
 */
export async function fitLabelsToLabelStock(
  source: ArrayBuffer | Uint8Array,
): Promise<Uint8Array> {
  const sourceDoc = await PDFDocument.load(source);
  const outputDoc = await PDFDocument.create();

  const pageWidth = LABEL_GEOMETRY.widthIn * PT_PER_IN;
  const pageHeight = LABEL_GEOMETRY.heightIn * PT_PER_IN;
  const insetX = LABEL_GEOMETRY.padXIn * PT_PER_IN;
  const insetY = VERTICAL_INSET_IN * PT_PER_IN;
  const boxWidth = pageWidth - insetX * 2;
  const boxHeight = pageHeight - insetY * 2;

  const embeddedPages = await outputDoc.embedPages(sourceDoc.getPages());

  for (const embeddedPage of embeddedPages) {
    const scale = Math.min(
      boxWidth / embeddedPage.width,
      boxHeight / embeddedPage.height,
    );
    const page = outputDoc.addPage([pageWidth, pageHeight]);
    page.drawPage(embeddedPage, {
      x: insetX,
      y: (pageHeight - embeddedPage.height * scale) / 2,
      xScale: scale,
      yScale: scale,
    });
  }

  return await outputDoc.save();
}
